import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import settingsService from '../../services/settingsService';
import { Download, Trash2, AlertTriangle, Check, X, Shield } from 'lucide-react';

const DataPrivacySection = () => {
  const { user } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleExportData = async () => {
    try {
      setExporting(true);
      setMessage({ type: '', text: '' });

      const data = await settingsService.exportUserData(user.id);
      
      // Create a blob and download
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `swirl-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setMessage({ type: 'success', text: 'Data exported successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error exporting data:', error);
      setMessage({ type: 'error', text: 'Failed to export data' });
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAllData = async () => {
    if (deleteConfirmText.toLowerCase() !== 'delete my data') {
      setMessage({ type: 'error', text: 'Please type "delete my data" to confirm' });
      return;
    }

    try {
      setDeleting(true);
      setMessage({ type: '', text: '' });

      await settingsService.deleteAllUserData(user.id);
      
      setShowDeleteConfirm(false);
      setMessage({ 
        type: 'success', 
        text: 'All data deleted successfully. You will be logged out shortly.' 
      });

      // Log out after a delay
      setTimeout(() => {
        window.location.href = '/';
      }, 3000);
    } catch (error) {
      console.error('Error deleting data:', error);
      setMessage({ type: 'error', text: 'Failed to delete data' });
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Data & Privacy</h2>
        <p className="text-gray-600 mt-1">
          Manage your data and privacy preferences
        </p>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`flex items-center space-x-2 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          {message.type === 'success' ? <Check size={20} /> : <X size={20} />}
          <span>{message.text}</span>
        </div>
      )}

      {/* Export Data */}
      <div className="border border-gray-200 rounded-lg p-6">
        <div className="flex items-start space-x-4">
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center flex-shrink-0">
            <Download size={24} className="text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Export Your Data
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Download a complete copy of your Swirl data including profiles, settings, focuses, memory items, conversations, and integrations.
            </p>
            <button
              onClick={handleExportData}
              disabled={exporting}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download size={18} />
              <span>{exporting ? 'Exporting...' : 'Export Data'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Privacy Information */}
      <div className="border border-gray-200 rounded-lg p-6">
        <div className="flex items-start space-x-4">
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center flex-shrink-0">
            <Shield size={24} className="text-green-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              How We Protect Your Data
            </h3>
            <ul className="text-sm text-gray-600 space-y-2">
              <li className="flex items-start space-x-2">
                <span className="text-green-600 mt-0.5">✓</span>
                <span>All data is encrypted in transit and at rest</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-green-600 mt-0.5">✓</span>
                <span>We never share your personal data with third parties</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-green-600 mt-0.5">✓</span>
                <span>You have full control over your data at all times</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-green-600 mt-0.5">✓</span>
                <span>Regular security audits and compliance checks</span>
              </li>
            </ul>
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-4 text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Read our Privacy Policy →
            </a>
          </div>
        </div>
      </div>

      {/* Delete All Data - Danger Zone */}
      <div className="pt-6 border-t border-gray-200">
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 rounded-lg bg-red-200 flex items-center justify-center flex-shrink-0">
              <AlertTriangle size={24} className="text-red-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-900 mb-2">
                Danger Zone
              </h3>
              <p className="text-sm text-red-800 mb-4">
                Permanently delete all your data from Swirl. This action cannot be undone and will:
              </p>
              <ul className="text-sm text-red-800 space-y-1 mb-4">
                <li>• Delete all your focuses, memory items, and conversations</li>
                <li>• Remove all uploaded content and files</li>
                <li>• Disconnect all integrations</li>
                <li>• Delete your account settings and preferences</li>
              </ul>
              
              {!showDeleteConfirm ? (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete All Data
                </button>
              ) : (
                <div className="space-y-3 bg-white bg-opacity-50 rounded-lg p-4">
                  <p className="text-sm font-semibold text-red-900">
                    Type "delete my data" to confirm:
                  </p>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    className="w-full px-3 py-2 border-2 border-red-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="delete my data"
                  />
                  <div className="flex space-x-2">
                    <button
                      onClick={handleDeleteAllData}
                      disabled={deleting || deleteConfirmText.toLowerCase() !== 'delete my data'}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {deleting ? 'Deleting...' : 'Confirm Delete'}
                    </button>
                    <button
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setDeleteConfirmText('');
                      }}
                      disabled={deleting}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataPrivacySection;





